import React from 'react';
import { Link } from 'react-router-dom';
import { ROUTER_LINKS } from '../common/consts/app-keys.const';
// Imported from the file rather than the barrel: the barrel also pulls in the
// Lottie loader, which is a lot of player for a page that never shows one.
import { NotFound } from '../common/components/404.component';
import { useRiserPage } from './feature/use-riser-page';

/**
 * The Riser product page.
 *
 * Laid out like the Eye and Subdivs pages, and fed completely differently. The
 * other two carry their copy in the frontend's i18n bundle, which is right for
 * a product anyone may read about. Riser is unannounced, so every word here
 * arrives from an endpoint that answers only an account holding the `riser`
 * grant. Without it there is no copy to draw and this renders the site's
 * ordinary not-found page, exactly as a mistyped URL would.
 *
 * The route is registered unconditionally, the way Standby's is. Hiding the
 * route would be no stronger, since the router ships to every visitor either
 * way, and it would make the granted case depend on load order.
 */
const RiserPageContainer: React.FC = () => {
  const { isLoading, page } = useRiserPage();

  // Nothing is known yet. Deliberately blank rather than a not-found that
  // would flash away a moment later for someone who does have access.
  if (isLoading) return <div className="w-full min-h-[60vh]" />;

  if (!page) return <NotFound />;

  return (
    <div className="w-full px-6 md:px-10 py-16 md:py-24">
      {/* Hero */}
      <section>
        {page.version && (
          <span className="inline-flex items-center gap-[5px] mb-5 px-[9px] py-[2px] rounded-[5px] bg-surface-hover border border-blue-500 text-[10.5px] font-mono font-medium text-blue tracking-wide">
            <span className="w-[5px] h-[5px] rounded-full bg-blue animate-pulse" />v
            {page.version}
          </span>
        )}
        <h1
          aria-label={`Squarebit ${page.hero.wordmark}`}
          className="hero-lockup text-content-dark"
        >
          {/* Same lockup as the other product pages: the wrapper shrink-wraps
              to the wordmark and the eyebrow's hairline rules stretch to its
              exact edges. */}
          <div className="w-fit">
            <div
              aria-hidden="true"
              className="flex items-center gap-[1em] font-display font-semibold uppercase text-content-muted-alt leading-none text-[clamp(10px,1.7vw,17px)] tracking-[0.42em] mb-[8px] md:mb-[12px]"
            >
              <span className="h-px flex-1 bg-current opacity-30 animate-hero-rule-l" />
              <span className="whitespace-nowrap -mr-[0.42em] animate-hero-eyebrow">
                Squarebit
              </span>
              <span className="h-px flex-1 bg-current opacity-30 animate-hero-rule-r" />
            </div>
            <span className="hero-wordmark block font-display font-extrabold uppercase leading-[0.9] tracking-[-0.045em] animate-hero-wordmark">
              {page.hero.wordmark}
            </span>
            <span className="block font-display font-semibold uppercase text-content-muted-alt mt-[8px] md:mt-[14px] pl-[4px] tracking-[0.34em] text-[clamp(14px,3.2vw,24px)] animate-hero-tagline">
              {page.hero.tagline}
            </span>
          </div>
        </h1>
        <h2 className="font-display font-bold text-[20px] md:text-[26px] tracking-[-0.02em] leading-[1.3] text-content-muted-alt mt-6 max-w-[600px]">
          {page.hero.title}
        </h2>
        <p className="text-content-muted-alt text-[16px] md:text-[18px] leading-[1.65] mt-5 max-w-[580px]">
          {page.hero.subtitle}
        </p>
        <div className="flex items-center gap-x-6 gap-y-3 mt-8 flex-wrap">
          <a
            href={page.appUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded-full bg-blue text-surface-base font-body font-semibold text-[15px] px-[26px] py-[13px] transition-all duration-200 hover:bg-blue-deep-1 hover:shadow-[0px_0px_0px_4px_rgba(93,225,255,0.25)]"
          >
            {page.hero.primaryCta}
          </a>
          <Link
            to={ROUTER_LINKS.CONTACT}
            className="font-body text-[14px] font-medium text-blue hover:text-blue-deep-1 transition-colors"
          >
            {page.hero.secondaryCta} →
          </Link>
        </div>
      </section>

      {/* What it does */}
      <section className="mt-20 md:mt-28">
        <h2 className="font-display font-bold text-[26px] md:text-[34px] tracking-[-0.02em] text-content-dark mb-3">
          {page.whatTitle}
        </h2>
        <p className="text-content-muted-alt text-[15px] md:text-[16px] leading-[1.6] max-w-[620px] mb-10">
          {page.whatBody}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px] md:gap-[20px]">
          {page.capabilities.map((c) => (
            <div
              key={c.id}
              className="bg-surface-base border border-border-brand rounded-[16px] p-[24px] md:p-[28px] shadow-[0_2px_12px_rgba(34,211,238,0.06)]"
            >
              <span className="block font-mono text-[11px] uppercase tracking-[0.12em] text-content-muted-alt mb-[10px]">
                {c.eyebrow}
              </span>
              <h3 className="font-display font-bold text-[18px] md:text-[20px] text-content-dark mb-[10px]">
                {c.heading}
              </h3>
              <p className="text-content-secondary text-[14px] md:text-[15px] leading-[1.7]">
                {c.body}
              </p>
              <div className="flex flex-wrap gap-[6px] mt-[16px]">
                {c.chips.map((chip) => (
                  <span
                    key={chip}
                    className="inline-flex items-center px-[9px] py-[3px] rounded-[6px] bg-surface-hover border border-border-brand font-mono text-[11px] text-content-muted-alt"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* What you export */}
      <section className="mt-20 md:mt-28 rounded-[20px] bg-[var(--subscription-brand-100)] border border-border-brand px-[24px] md:px-[48px] py-[36px] md:py-[48px]">
        <h2 className="font-display font-bold text-[26px] md:text-[34px] tracking-[-0.02em] text-content-dark mb-3">
          {page.document.title}
        </h2>
        <p className="text-content-secondary text-[15px] md:text-[16px] leading-[1.7] max-w-[720px] mb-8">
          {page.document.body}
        </p>
        <pre className="overflow-x-auto rounded-[10px] bg-surface-base border border-border-brand px-[16px] py-[12px] font-mono text-[12px] md:text-[13px] text-content-dark mb-8">
          {page.document.formula}
        </pre>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:gap-[28px]">
          <div>
            <h3 className="font-display font-bold text-[17px] md:text-[19px] text-content-muted-alt mb-[8px]">
              {page.document.beforeTitle}
            </h3>
            <p className="text-content-secondary text-[14px] md:text-[15px] leading-[1.7]">
              {page.document.beforeBody}
            </p>
          </div>
          <div>
            <h3 className="font-display font-bold text-[17px] md:text-[19px] text-blue mb-[8px]">
              {page.document.afterTitle}
            </h3>
            <p className="text-content-secondary text-[14px] md:text-[15px] leading-[1.7]">
              {page.document.afterBody}
            </p>
          </div>
        </div>
      </section>

      {/* What it reads and writes */}
      <section className="mt-20 md:mt-28">
        <h2 className="font-display font-bold text-[26px] md:text-[34px] tracking-[-0.02em] text-content-dark mb-3">
          {page.formats.title}
        </h2>
        <p className="text-content-muted-alt text-[15px] md:text-[16px] leading-[1.6] max-w-[620px] mb-6">
          {page.formats.body}
        </p>
        <div className="flex flex-wrap gap-[8px]">
          {page.formats.reads.map((ext) => (
            <span
              key={ext}
              className="inline-flex items-center px-[11px] py-[4px] rounded-[6px] bg-surface-hover border border-blue-500 font-mono text-[12px] text-blue"
            >
              {ext}
            </span>
          ))}
        </div>
        <p className="text-content-secondary text-[14px] md:text-[15px] leading-[1.7] mt-6">
          {page.formats.writes}
        </p>
      </section>

      {/* Access */}
      <section className="mt-20 md:mt-28 rounded-[16px] border border-border-brand bg-surface-base px-[24px] md:px-[40px] py-[32px] md:py-[40px] shadow-[0_2px_12px_rgba(34,211,238,0.06)]">
        <h2 className="font-display font-bold text-[22px] md:text-[28px] tracking-[-0.02em] text-content-dark mb-3">
          {page.access.title}
        </h2>
        <p className="text-content-secondary text-[15px] md:text-[16px] leading-[1.7] max-w-[620px] mb-7">
          {page.access.body}
        </p>
        <a
          href={page.appUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center rounded-full bg-blue text-surface-base font-body font-semibold text-[15px] px-[26px] py-[13px] transition-all duration-200 hover:bg-blue-deep-1 hover:shadow-[0px_0px_0px_4px_rgba(93,225,255,0.25)]"
        >
          {page.access.label}
        </a>
      </section>

      {/* Final CTA */}
      <section className="mt-20 md:mt-28 mb-4 text-center">
        <h2 className="font-display font-extrabold text-[28px] md:text-[40px] tracking-[-0.02em] text-content-dark mb-4">
          {page.cta.title}
        </h2>
        <p className="text-content-muted-alt text-[15px] md:text-[17px] leading-[1.6] max-w-[560px] mx-auto mb-8">
          {page.cta.body}
        </p>
        <Link
          to={ROUTER_LINKS.CONTACT}
          className="inline-flex items-center rounded-full bg-blue text-surface-base font-body font-semibold text-[16px] px-[30px] py-[14px] transition-all duration-200 hover:bg-blue-deep-1 hover:shadow-[0px_0px_0px_4px_rgba(93,225,255,0.25)]"
        >
          {page.cta.label}
        </Link>
      </section>
    </div>
  );
};

export default RiserPageContainer;
